import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { PermissionsProvider, usePermissions } from "@/contexts/PermissionsContext";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import MyQueue from "@/pages/MyQueue";
import Cases from "@/pages/Cases";
import CaseDetail from "@/pages/CaseDetail";
import NewCase from "@/pages/NewCase";
import QueueBoard from "@/pages/QueueBoard";
import Allocation from "@/pages/Allocation";
import Workforce from "@/pages/Workforce";
import EmployeeDetail from "@/pages/EmployeeDetail";
import Exceptions from "@/pages/Exceptions";
import Reports from "@/pages/Reports";
import Analytics from "@/pages/Analytics";
import Notifications from "@/pages/Notifications";
import Admin from "@/pages/Admin";
import Profile from "@/pages/Profile";
import "@/index.css";

function Protected({ children, roles }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="h-screen flex items-center justify-center bg-background text-muted-foreground mono">Loading…</div>;
  if (user === false) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function RouteRoot() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route element={<Protected><AppLayout /></Protected>}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/my-queue" element={<Protected module="my_queue"><MyQueue /></Protected>} />
        <Route path="/cases" element={<Protected module="cases"><Cases /></Protected>} />
        <Route path="/cases/new" element={<Protected module="new_case"><NewCase /></Protected>} />
        <Route path="/cases/:id" element={<Protected module="cases"><CaseDetail /></Protected>} />
        <Route path="/queue" element={<Protected module="queue_board"><QueueBoard /></Protected>} />
        <Route path="/allocation" element={<Protected module="allocation"><Allocation /></Protected>} />
        <Route path="/workforce" element={<Protected module="workforce"><Workforce /></Protected>} />
        <Route path="/workforce/:id" element={<Protected module="workforce"><EmployeeDetail /></Protected>} />
        <Route path="/exceptions" element={<Protected module="exceptions"><Exceptions /></Protected>} />
        <Route path="/reports" element={<Protected module="reports"><Reports /></Protected>} />
        <Route path="/analytics" element={<Protected module="analytics"><Analytics /></Protected>} />
        <Route path="/notifications" element={<Protected module="notifications"><Notifications /></Protected>} />
        <Route path="/admin" element={<Protected roles={["manager"]} module="admin"><Admin /></Protected>} />
        <Route path="/profile" element={<Profile />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PermissionsProvider>
          <BrowserRouter>
            <RouteRoot />
            <Toaster position="top-right" richColors closeButton />
          </BrowserRouter>
        </PermissionsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
